import { BudgetRowValues } from '@/components/ModifyBudgetRow/ModifyBudgetRow';
import { WithLoader } from '@/components/WithLoader';
import type { ZeroInvestmentAdjustmentInfo } from '@/dashboards/BudgetOptimization/core/budgetSessionRules';
import { useBudgetOptimisationSession } from '@/dashboards/BudgetOptimization/core/useBudgetOptimisationSession';
import {
    optimisationPeriod,
    useBudgetOptimizationData,
    UseBudgetOptimizationQueryResponse,
} from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationData';
import { useBudgetOptimizationUrl } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationUrl';
import { SubmitFunc } from '@/dashboards/BudgetOptimization/types';
import type { OptimisationRun } from '@/dashboards/shared/optimisation/optimisationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { OptimisationBase } from '@/optimizer';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { type Message } from '@analytical-alley/opt-wasm';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { createContext } from 'use-context-selector';

interface BudgetOptimizationContextValue {
    margin: number;
    onSubmit: SubmitFunc;
    recalculate: () => void;
    resetVariables: () => void;
    setMargin: (margin: number) => void;
    variableValues: { [key: string]: BudgetRowValues };
    // Draft target being edited in the control panel.
    optimizationBase: OptimisationBase;
    budget: number | undefined;
    setBudget: (value: number | undefined) => void;
    setTarget: (base: OptimisationBase, budget: number | undefined) => void;
    // Base the rendered results were optimised for.
    committedOptimizationBase: OptimisationBase;
    committed: OptimisationRun | null;
    error: Error | null;
    dismissError: () => void;
    isDirty: boolean;
    messages: Message[];
    flushMessages: () => void;
    isLoading: boolean;
    isFetching: boolean;
    isOptimizing: boolean;
    disabledChannels: string[];
}

export const BudgetOptimizationContext = createContext<
    BudgetOptimizationContextValue &
        Pick<UseBudgetOptimizationQueryResponse, 'data'>
>({
    margin: 100,
    onSubmit: () => {},
    recalculate: () => {},
    resetVariables: () => {},
    setMargin: () => {},
    variableValues: {},
    optimizationBase: 'budget',
    budget: undefined,
    setBudget: () => {},
    setTarget: () => {},
    committedOptimizationBase: 'budget',
    committed: null,
    error: null,
    dismissError: () => {},
    isDirty: false,
    messages: [],
    flushMessages: () => {},
    isLoading: false,
    isFetching: false,
    isOptimizing: false,
    disabledChannels: [],
    data: {
        previous: undefined,
        optimized: undefined,
        highestVariableValue: 0,
        highestCategoryValue: 0,
        suggestedBudgetData: [],
        budgetByCategoryData: {
            previous: [],
            simulated: [],
        },
        monthlyMediaInvestment: {
            monthlyMediaData: [],
            highestValue: 0,
        },
        initialAllocation: {},
        budgetByVariableData: {},
        contributionTotal: 0,
        previousPeriodInvestmentTotal: 0,
        simulatedInvestmentTotal: 0,
        diminishingPoints: {},
        expectedContributionByCategoryData: {},
        optimizationStartDate: '',
        optimizationEndDate: '',
        historicDataStartDate: '',
        historicDataEndDate: '',
    },
});

const EMPTY_MESSAGES: Message[] = [];

export const BudgetOptimizationContextProvider = ({
    children,
    onZeroInvestmentAdjustment,
}: {
    children: ReactNode;
    onZeroInvestmentAdjustment?: (info: ZeroInvestmentAdjustmentInfo) => void;
}) => {
    const { search, setSearch } = useBudgetOptimizationUrl();
    const { optimizer, optimizerReady, rules, setDashboardType } =
        useOptimizer();
    const {
        dashboard: { yearlyBudgetOptimisationPeriod },
        variableNameMap,
        disabledChannelsRules,
    } = useDashboardGlobalContext();

    useEffect(() => {
        setDashboardType('budget');
    }, [setDashboardType]);

    const [margin, setMargin] = useState<number>(100);

    const period = optimisationPeriod(yearlyBudgetOptimisationPeriod);
    const session = useBudgetOptimisationSession({
        configRules: rules,
        optimizer,
        optimizerReady,
        search: {
            budget: search.budget,
            optimizationBase: search.optimizationBase,
        },
        setSearch,
        startDate: period.startDate,
        endDate: period.endDate,
        variableNameMap,
        onZeroInvestmentAdjustment,
    });
    const {
        draft,
        committed,
        error,
        isDirty,
        isRunning,
        isInitialLoading,
        setTarget,
        setChannel,
        resetDraft,
        dismissError,
        runOptimization,
    } = session;

    // Nothing committed yet means nothing to fall back to: let the route's error screen
    // handle it instead of leaving the page behind a loader.
    if (error && committed === null) {
        throw error;
    }

    const {
        data,
        isLoading: isDataLoading,
        isFetching: isDataFetching,
    } = useBudgetOptimizationData(committed?.result);

    const [dismissedRunAt, setDismissedRunAt] = useState<number | null>(null);
    const messages =
        committed && committed.finishedAt !== dismissedRunAt
            ? committed.messages
            : EMPTY_MESSAGES;
    const flushMessages = useCallback(
        () => setDismissedRunAt(committed?.finishedAt ?? null),
        [committed?.finishedAt],
    );

    const optimizationBase =
        draft?.optimizationBase ?? search.optimizationBase ?? 'budget';
    const committedOptimizationBase =
        committed?.inputs.optimizationBase ?? optimizationBase;

    const setBudget = useCallback(
        (value: number | undefined) => setTarget(optimizationBase, value),
        [setTarget, optimizationBase],
    );

    const onSubmit: SubmitFunc = useCallback(
        (key: string, value: BudgetRowValues | number | null) => {
            if (key === 'margin') return;
            setChannel(key, value as BudgetRowValues);
        },
        [setChannel],
    );

    const disabledChannels: string[] = useMemo(() => {
        return disabledChannelsRules.reduce((acc, rule): string[] => {
            if (rule.target === 'budget') {
                acc.push(rule.channel);
            }
            return acc;
        }, [] as string[]);
    }, [disabledChannelsRules]);

    const isLoading = isInitialLoading || isDataLoading;
    const isFetching = isRunning || isDataFetching;
    const isOptimizing = isInitialLoading || isRunning;

    return (
        <BudgetOptimizationContext.Provider
            value={{
                margin,
                setMargin,
                onSubmit,
                recalculate: runOptimization,
                resetVariables: resetDraft,
                data,
                messages,
                flushMessages,
                isLoading,
                isFetching,
                isOptimizing,
                variableValues: draft?.channels ?? {},
                optimizationBase,
                budget: draft?.budget,
                setBudget,
                setTarget,
                committedOptimizationBase,
                committed,
                error,
                dismissError,
                isDirty,
                disabledChannels,
            }}
        >
            <WithLoader
                isLoading={isOptimizing || isDataLoading}
                isFetching={isDataFetching}
                loadingMessage="Optimizing yearly budget ..."
                delayedMessage="Budget optimization calculations may take a while depending on the complexity of your data. Please be patient while we process your request."
                delayedMessageThreshold={2000}
            >
                {children}
            </WithLoader>
        </BudgetOptimizationContext.Provider>
    );
};
